import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ASSETS } from "@/lib/brand";

const empty = {
  title: "", slug: "", excerpt: "", body: "", publication: "", cover_url: "", external_url: "", published: true,
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("articles");
  const [articles, setArticles] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [editing, setEditing] = useState(null); // null=new (closed), 'new', or article object
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const loadArticles = () => api.get("/articles?include_unpublished=true").then((r) => setArticles(r.data || []));
  const loadSubs = () => api.get("/submissions").then((r) => setSubmissions(r.data || []));
  const loadSubscribers = () => api.get("/newsletter").then((r) => setSubscribers(r.data || []));

  useEffect(() => {
    loadArticles().catch(() => {});
    loadSubs().catch(() => {});
    loadSubscribers().catch(() => {});
  }, []);

  const unreadCount = useMemo(() => submissions.filter((s) => !s.read).length, [submissions]);

  const startNew = () => { setForm(empty); setEditing("new"); setError(""); };
  const startEdit = (a) => { setForm({ ...empty, ...a }); setEditing(a); setError(""); };
  const cancel = () => { setEditing(null); setForm(empty); setError(""); };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      if (editing === "new") {
        await api.post("/articles", form);
      } else {
        await api.put(`/articles/${editing.id}`, form);
      }
      await loadArticles();
      cancel();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete "${a.title}"?`)) return;
    try {
      await api.delete(`/articles/${a.id}`);
      await loadArticles();
    } catch (err) {
      alert(formatApiError(err));
    }
  };

  const removeSub = async (s) => {
    if (!window.confirm(`Delete inquiry from ${s.name}?`)) return;
    try {
      await api.delete(`/submissions/${s.id}`);
      await loadSubs();
    } catch (err) {
      alert(formatApiError(err));
    }
  };

  const markRead = async (s) => {
    try {
      await api.patch(`/submissions/${s.id}/read`);
      await loadSubs();
    } catch (_) {}
  };

  const removeSubscriber = async (s) => {
    if (!window.confirm(`Remove ${s.email} from the newsletter?`)) return;
    try {
      await api.delete(`/newsletter/${s.id}`);
      await loadSubscribers();
    } catch (err) {
      alert(formatApiError(err));
    }
  };

  const exportSubscribersCsv = () => {
    const rows = [["email", "source", "created_at"], ...subscribers.map(s => [s.email, s.source, s.created_at])];
    const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `subscribers_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const signOut = async () => {
    await logout();
    navigate("/admin/login");
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-navy text-silver">
      {/* Admin Topbar (replaces public nav) */}
      <header className="sticky top-0 z-40 bg-[#050A15]/85 backdrop-blur-xl border-b hairline">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={ASSETS.monogram} alt="MS" className="h-9 w-9 rounded-sm border hairline" />
            <div className="hidden md:block">
              <p className="font-overline text-silver-muted">Control Room</p>
              <p className="font-display text-base text-silver">Office of Miguel Santana</p>
            </div>
          </div>
          <div className="flex items-center gap-6">
            <span className="hidden md:inline font-overline text-silver-muted">{user?.email}</span>
            <Link to="/" className="font-overline text-silver-muted hover:text-silver transition-colors">View Site ↗</Link>
            <button onClick={signOut} data-testid="admin-signout" className="btn-editorial">Sign Out</button>
          </div>
        </div>
      </header>

      <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-12">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-white/10 border hairline">
          <Stat k={articles.length} label="Articles" />
          <Stat k={articles.filter(a => a.published).length} label="Published" />
          <Stat k={submissions.length} label="Inquiries" />
          <Stat k={unreadCount} label="Unread" accent />
          <Stat k={subscribers.length} label="Subscribers" />
        </div>

        {/* Tabs */}
        <div className="mt-12 border-b hairline flex gap-2 flex-wrap">
          <TabBtn active={tab === "articles"} onClick={() => setTab("articles")} testId="tab-articles">Press Articles</TabBtn>
          <TabBtn active={tab === "submissions"} onClick={() => setTab("submissions")} testId="tab-submissions">Inquiries</TabBtn>
          <TabBtn active={tab === "newsletter"} onClick={() => setTab("newsletter")} testId="tab-newsletter">Newsletter</TabBtn>
        </div>

        {tab === "articles" && (
          <section className="mt-10" data-testid="articles-panel">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-3xl text-silver tracking-tight">Press Articles</h2>
              {!editing && (
                <button onClick={startNew} data-testid="article-new" className="btn-editorial btn-editorial-filled">+ New Article</button>
              )}
            </div>

            {editing && (
              <form onSubmit={save} className="mb-12 border hairline p-6 md:p-10 bg-navy-surface" data-testid="article-form">
                <p className="font-overline text-champagne mb-2">{editing === "new" ? "New Article" : `Editing · ${editing.title}`}</p>
                <h3 className="font-display text-2xl text-silver mb-8">Article composer</h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8">
                  <Field label="Title" testId="article-title">
                    <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="input-editorial" />
                  </Field>
                  <Field label="Slug (optional · auto-generated from title)" testId="article-slug">
                    <input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} className="input-editorial" placeholder="e.g. court-street-vegas-launch" />
                  </Field>
                  <Field label="Publication" testId="article-publication">
                    <input value={form.publication} onChange={(e) => setForm({ ...form, publication: e.target.value })} className="input-editorial" placeholder="Forbes, The Atlantic…" />
                  </Field>
                  <Field label="Cover Image URL" testId="article-cover">
                    <input value={form.cover_url} onChange={(e) => setForm({ ...form, cover_url: e.target.value })} className="input-editorial" placeholder="https://…" />
                  </Field>
                  <Field label="External URL" testId="article-external">
                    <input value={form.external_url} onChange={(e) => setForm({ ...form, external_url: e.target.value })} className="input-editorial" placeholder="https://…" />
                  </Field>
                </div>

                <Field label="Excerpt" testId="article-excerpt" className="mt-8">
                  <input value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} className="input-editorial" placeholder="Short dek shown on listing pages" />
                </Field>

                <Field label="Body" testId="article-body" className="mt-8">
                  <textarea rows={10} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="input-editorial" placeholder="Use blank lines to separate paragraphs." />
                </Field>

                <label className="mt-6 flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} data-testid="article-published" />
                  <span className="font-overline text-silver">Published (visible on public Journal)</span>
                </label>

                {error && <p className="mt-6 font-body text-red-300 text-sm">{error}</p>}

                <div className="mt-10 flex items-center gap-4">
                  <button type="submit" disabled={busy} className="btn-editorial btn-editorial-filled" data-testid="article-save">
                    {busy ? "Saving…" : editing === "new" ? "Publish Article" : "Save Changes"}
                  </button>
                  <button type="button" onClick={cancel} className="btn-editorial" data-testid="article-cancel">Cancel</button>
                </div>
              </form>
            )}

            <div className="border-t hairline">
              {articles.length === 0 && (
                <p className="py-12 font-body text-silver-muted">No articles yet. Compose your first one above.</p>
              )}
              {articles.map((a) => (
                <div key={a.id} data-testid={`article-row-${a.id}`} className="grid grid-cols-12 gap-4 items-center py-6 border-b hairline">
                  <div className="col-span-12 md:col-span-2">
                    <p className="font-overline text-champagne">{a.publication || "—"}</p>
                    <p className="font-overline text-silver-muted mt-1">{new Date(a.created_at).toLocaleDateString()}</p>
                  </div>
                  <div className="col-span-12 md:col-span-7">
                    <h3 className="font-display text-xl text-silver leading-tight">{a.title}</h3>
                    <p className="font-body text-silver-muted text-sm mt-1 line-clamp-1">{a.excerpt}</p>
                    {a.slug && (
                      <p className="font-overline text-silver-muted/70 mt-2 text-[0.65rem]">/journal/{a.slug}</p>
                    )}
                  </div>
                  <div className="col-span-6 md:col-span-1">
                    <span className={`font-overline ${a.published ? "text-champagne" : "text-silver-muted"}`}>
                      {a.published ? "Live" : "Draft"}
                    </span>
                  </div>
                  <div className="col-span-6 md:col-span-2 flex md:justify-end gap-3">
                    <button onClick={() => startEdit(a)} className="font-overline text-silver hover:text-champagne transition-colors" data-testid={`article-edit-${a.id}`}>Edit</button>
                    <button onClick={() => remove(a)} className="font-overline text-red-300 hover:text-red-400 transition-colors" data-testid={`article-delete-${a.id}`}>Delete</button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {tab === "submissions" && (
          <section className="mt-10" data-testid="submissions-panel">
            <h2 className="font-display text-3xl text-silver tracking-tight mb-6">Inquiries</h2>
            <div className="border-t hairline">
              {submissions.length === 0 && (
                <p className="py-12 font-body text-silver-muted">No inquiries yet.</p>
              )}
              {submissions.map((s) => (
                <div key={s.id} data-testid={`submission-row-${s.id}`} className="grid grid-cols-12 gap-4 py-8 border-b hairline">
                  <div className="col-span-12 md:col-span-3">
                    <p className="font-display text-xl text-silver">{s.name}</p>
                    <p className="font-overline text-silver-muted mt-1">{s.email}</p>
                    {s.company && <p className="font-overline text-silver-muted mt-1">{s.company}</p>}
                    <p className="font-overline text-champagne mt-2">{s.interest}</p>
                  </div>
                  <div className="col-span-12 md:col-span-7">
                    <p className="font-overline text-silver-muted">{new Date(s.created_at).toLocaleString()}</p>
                    <h3 className="font-display text-lg text-silver mt-1">{s.subject || "(no subject)"}</h3>
                    <p className="font-body text-silver-muted text-sm mt-3 whitespace-pre-wrap">{s.message}</p>
                  </div>
                  <div className="col-span-12 md:col-span-2 flex md:flex-col md:items-end gap-3">
                    {!s.read && (
                      <button onClick={() => markRead(s)} data-testid={`submission-read-${s.id}`} className="font-overline text-champagne hover:text-silver transition-colors">Mark Read</button>
                    )}
                    <a href={`mailto:${s.email}?subject=Re: ${encodeURIComponent(s.subject || "Your inquiry")}`} className="font-overline text-silver hover:text-champagne transition-colors" data-testid={`submission-reply-${s.id}`}>Reply ↗</a>
                    <button onClick={() => removeSub(s)} data-testid={`submission-delete-${s.id}`} className="font-overline text-red-300 hover:text-red-400 transition-colors">Delete</button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {tab === "newsletter" && (
          <section className="mt-10" data-testid="newsletter-panel">
            <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
              <h2 className="font-display text-3xl text-silver tracking-tight">Newsletter Subscribers</h2>
              <button onClick={exportSubscribersCsv} disabled={subscribers.length === 0} data-testid="newsletter-export" className="btn-editorial disabled:opacity-40">
                Export CSV
              </button>
            </div>
            <div className="border-t hairline">
              {subscribers.length === 0 && (
                <p className="py-12 font-body text-silver-muted">No subscribers yet. The list will populate as visitors join from the home page or footer.</p>
              )}
              {subscribers.map((s, i) => (
                <div key={s.id} data-testid={`subscriber-row-${s.id}`} className="grid grid-cols-12 gap-4 items-center py-5 border-b hairline">
                  <div className="col-span-1 md:col-span-1">
                    <p className="font-overline text-silver-muted">{String(i + 1).padStart(3, "0")}</p>
                  </div>
                  <div className="col-span-11 md:col-span-6">
                    <p className="font-display text-lg text-silver">{s.email}</p>
                  </div>
                  <div className="col-span-6 md:col-span-2">
                    <p className="font-overline text-champagne">{s.source}</p>
                  </div>
                  <div className="col-span-6 md:col-span-2">
                    <p className="font-overline text-silver-muted">{new Date(s.created_at).toLocaleDateString()}</p>
                  </div>
                  <div className="col-span-12 md:col-span-1 md:text-right">
                    <button onClick={() => removeSubscriber(s)} className="font-overline text-red-300 hover:text-red-400 transition-colors" data-testid={`subscriber-delete-${s.id}`}>Remove</button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

function Stat({ k, label, accent }) {
  return (
    <div className="bg-navy p-6 md:p-8">
      <p className={`font-display text-3xl md:text-5xl tracking-tighter ${accent ? "text-champagne" : "text-silver"}`}>{k}</p>
      <p className="mt-2 font-overline text-silver-muted">{label}</p>
    </div>
  );
}

function TabBtn({ active, onClick, children, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={`px-6 py-3 font-overline border-b-2 transition-all ${
        active ? "border-silver text-silver" : "border-transparent text-silver-muted hover:text-silver"
      }`}
    >
      {children}
    </button>
  );
}

function Field({ label, children, testId, className = "" }) {
  return (
    <div className={className} data-testid={testId}>
      <label className="font-overline text-silver-muted block mb-1">{label}</label>
      {children}
    </div>
  );
}
